import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";

// 🌈 Enhanced Status Badge Component
const StatusBadge = ({ status }) => {
  const normalized = (status || "").toLowerCase().replace(/\s+/g, "_");
  let style = {
    padding: "6px 12px",
    borderRadius: "12px",
    fontWeight: 600,
    fontSize: "0.8rem",
    display: "inline-block",
    textTransform: "capitalize",
    color: "#fff",
    boxShadow: "0 0 8px rgba(0,0,0,0.3)",
  };

  switch (normalized) {
    case "available":
      style.background = "linear-gradient(135deg, #16A34A, #22C55E)";
      style.boxShadow = "0 0 10px rgba(34,197,94,0.5)";
      break;
    case "assigned_to_agent":
      style.background = "linear-gradient(135deg, #0EA5E9, #38BDF8)";
      style.boxShadow = "0 0 10px rgba(14,165,233,0.5)";
      break;
    case "assigned_to_customer":
      style.background = "linear-gradient(135deg, #FACC15, #FBBF24)";
      style.color = "#1E293B";
      style.boxShadow = "0 0 10px rgba(250,204,21,0.6)";
      break;
    case "in_repair":
      style.background = "linear-gradient(135deg, #DC2626, #EF4444)";
      style.boxShadow = "0 0 10px rgba(239,68,68,0.5)";
      break;
    case "decommissioned":
      style.background = "linear-gradient(135deg, #6B7280, #9CA3AF)";
      style.boxShadow = "0 0 10px rgba(107,114,128,0.5)";
      break;
    default:
      style.background = "linear-gradient(135deg, #9CA3AF, #D1D5DB)";
      style.color = "#1E293B";
      style.boxShadow = "0 0 10px rgba(156,163,175,0.5)";
  }

  return <span style={style}>{status || "Unknown"}</span>;
};

function DeviceViewPage() {
  const { deviceId } = useParams();
  const [device, setDevice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const API_BASE_URL =
    process.env.REACT_APP_API_BASE_URL || "https://installmentzone.com/api";

  useEffect(() => {
    const fetchDevice = async () => {
      if (!deviceId) {
        setError("Device ID is missing in the URL.");
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        setError(null);

        const url = `${API_BASE_URL}/devices/view.php?id=${deviceId}`;
        const response = await fetch(url);
        if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        const result = await response.json();

        if (result.success && result.data) {
          setDevice(result.data);
        } else {
          setError(result.error || "Device not found or request failed.");
        }
      } catch (err) {
        setError(err.message || "An error occurred while fetching device details.");
      } finally {
        setLoading(false);
      }
    };

    fetchDevice();
  }, [deviceId, API_BASE_URL]);

  const formatStatusDisplay = (status) => {
    if (!status) return "N/A";
    return status
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateString;
    }
  };

  if (loading)
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "linear-gradient(135deg, #1E3A8A, #3B82F6, #60A5FA)",
          color: "#fff",
          fontSize: "1.2rem",
        }}
      >
        Loading device details...
      </div>
    );

  if (error)
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "linear-gradient(135deg, #1E3A8A, #3B82F6, #60A5FA)",
          color: "#FCA5A5",
          fontSize: "1.2rem",
        }}
      >
        Error: {error}
      </div>
    );

  if (!device)
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "linear-gradient(135deg, #1E3A8A, #3B82F6, #60A5FA)",
          color: "#E0E7FF",
          fontSize: "1.2rem",
        }}
      >
        Device details not found.
      </div>
    );

  return (
    <div
      style={{
        minHeight: "100vh",
        padding: "40px 20px",
        background: "linear-gradient(135deg, #1E3A8A, #3B82F6, #60A5FA)",
        fontFamily: "Segoe UI, sans-serif",
        color: "#fff",
        overflowY: "auto",
      }}
    >
      <div
        style={{
          background: "rgba(255,255,255,0.08)",
          padding: "30px",
          borderRadius: "15px",
          maxWidth: "900px",
          margin: "0 auto",
          boxShadow: "0 6px 18px rgba(0,0,0,0.3)",
          backdropFilter: "blur(8px)",
        }}
      >
        <h2 style={{ textAlign: "center", marginBottom: "25px" }}>
          Device Details
        </h2>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 2fr",
            gap: "15px 25px",
            marginBottom: "25px",
          }}
        >
          <div className="detail-label">Device ID:</div>
          <div className="detail-value">{device.id || "N/A"}</div>

          <div className="detail-label">Name:</div>
          <div className="detail-value">{device.device_name || "N/A"}</div>

          <div className="detail-label">IMEI:</div>
          <div className="detail-value">{device.imei || "N/A"}</div>

          <div className="detail-label">Status:</div>
          <div className="detail-value">
            <StatusBadge status={device.status} />
          </div>

          {device.created_at && (
            <>
              <div className="detail-label">Added On:</div>
              <div className="detail-value">{formatDate(device.created_at)}</div>
            </>
          )}
          {device.updated_at && device.updated_at !== device.created_at && (
            <>
              <div className="detail-label">Last Updated:</div>
              <div className="detail-value">{formatDate(device.updated_at)}</div>
            </>
          )}
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: "15px",
            marginTop: "20px",
          }}
        >
          <button
            onClick={() => navigate("/devices")}
            style={{
              background: "#2563EB",
              color: "#fff",
              border: "none",
              borderRadius: "8px",
              padding: "10px 20px",
              cursor: "pointer",
              fontWeight: 600,
            }}
          >
            Back to List
          </button>
          <Link
            to={`/devices/edit/${deviceId}`}
            style={{
              background: "#CA8A04",
              color: "#fff",
              border: "none",
              borderRadius: "8px",
              padding: "10px 20px",
              cursor: "pointer",
              fontWeight: 600,
              textDecoration: "none",
            }}
          >
            Edit Device
          </Link>
        </div>
      </div>
    </div>
  );
}

export default DeviceViewPage;
